import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { BookOpen, Plus, Smile, Frown, Meh } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { SpeechToTextButton } from '../components/SpeechToTextButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MOODS = [
  { value: 'happy', icon: Smile, color: 'text-emerald-400' },
  { value: 'neutral', icon: Meh, color: 'text-amber-400' },
  { value: 'sad', icon: Frown, color: 'text-blue-400' },
];

export default function CareJournals() {
  const { user } = useAuth();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ content: '', mood: 'neutral', activities: '', client_id: '' });
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchJournals();
    if (['agency_admin', 'ceo', 'caregiver'].includes(user?.role)) fetchClients();
  }, [user]);

  const fetchJournals = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/care-journals`, { withCredentials: true });
      setJournals(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/by-org?role=client`, { withCredentials: true });
      setClients(data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async () => {
    if (!form.content) return toast.error('Content required');
    try {
      const payload = { ...form, activities: form.activities ? form.activities.split(',').map(a => a.trim()) : [] };
      await axios.post(`${API_URL}/api/care-journals`, payload, { withCredentials: true });
      toast.success('Journal entry added');
      setDialogOpen(false);
      setForm({ content: '', mood: 'neutral', activities: '', client_id: '' });
      fetchJournals();
    } catch (err) { toast.error('Failed'); }
  };

  const MoodIcon = ({ mood }) => {
    const m = MOODS.find(x => x.value === mood) || MOODS[1];
    return <m.icon className={`w-5 h-5 ${m.color}`} />;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="care-journals-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <BookOpen className="w-7 h-7 inline mr-3 text-[#00A6A0]" />Care Journals
            </h1>
            <p className="text-muted-foreground mt-1">Daily activity logs and care notes</p>
          </div>
          {['caregiver', 'agency_admin', 'ceo'].includes(user?.role) && (
            <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="add-journal-btn"><Plus className="w-4 h-4 mr-2" />New Entry</Button>
          )}
        </div>

        {journals.length === 0 ? (
          <div className="text-center py-16"><BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No journal entries yet</p></div>
        ) : (
          <div className="space-y-3">
            {journals.map(j => (
              <Card key={j.id} className="glass-card border-border" data-testid={`journal-${j.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <MoodIcon mood={j.mood} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-foreground font-medium">{j.caregiver_name}</span>
                        <span className="text-[10px] text-muted-foreground">{new Date(j.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{j.content}</p>
                      {j.activities?.length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {j.activities.map((a, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">{a}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="glass-card border-border max-w-md">
            <DialogHeader><DialogTitle className="text-foreground">New Journal Entry</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {clients.length > 0 && (
                <div><label className="text-xs text-muted-foreground">Client</label>
                  <select value={form.client_id} onChange={e => setForm(p => ({...p, client_id: e.target.value}))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div><label className="text-xs text-muted-foreground">Mood</label>
                <div className="flex gap-3 mt-1">
                  {MOODS.map(m => (
                    <button key={m.value} onClick={() => setForm(p => ({...p, mood: m.value}))}
                      className={`p-3 rounded-lg border transition-colors ${form.mood === m.value ? 'border-[#00A6A0] bg-[#00A6A0]/10' : 'border-border hover:border-[#3A444A]'}`}>
                      <m.icon className={`w-6 h-6 ${m.color}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div><label className="text-xs text-muted-foreground flex items-center justify-between">Journal Entry
                <SpeechToTextButton compact onTranscript={text => setForm(p => ({...p, content: p.content ? p.content + ' ' + text : text}))} />
              </label>
                <textarea value={form.content} onChange={e => setForm(p => ({...p, content: e.target.value}))} placeholder="What happened during the shift... (or tap mic to dictate)" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[120px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none" data-testid="journal-content-input" /></div>
              <div><label className="text-xs text-muted-foreground">Activities (comma-separated)</label>
                <Input value={form.activities} onChange={e => setForm(p => ({...p, activities: e.target.value}))} placeholder="e.g., Walking, Reading, Lunch" className="bg-muted border-border text-foreground" /></div>
              <Button className="w-full btn-primary" onClick={handleSubmit} data-testid="save-journal-btn">Save Entry</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
